import { SelectionChangedEvent } from "ag-grid-community";
import { INITIAL_ALLIANCE_COMP_COL_DEF, INITIAL_COMPANY_COL_DEF, INITIAL_COMPANY_COL_DEF_AQCT, INITIAL_MASTER_COMP_COL_DEF, transferRowData } from "presentation/constant/Company/CompanyColumnDefinition";
import { CompanyConstant } from "presentation/constant/Company/CompanyConstant";
import { EMPTY_COMPANY_SEARCH_CRITERIA } from "presentation/constant/Company/CompanySearchCriteria";
import { useCompanyMaintenanceVM } from "presentation/hook/Company/useCompanyMaintenanceVM";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCompanyMaintenanceTracked } from "presentation/store/Company/CompanyMaintenanceProvider";
import { commonColumnDef, commonTransferRowData } from "presentation/view/components/TableWrapper/BasicTableConstants";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Core } from "veronica-ui-component";
import { GroupButtons, Loader } from "veronica-ui-component/dist/component/core";


const CompanyTablePanel: React.FC = () => {
    const [companyState] = useCompanyMaintenanceTracked();
    const companyMaintenanceVM = useCompanyMaintenanceVM();
    const { isCompany, isCompanyCount, isMasterCompany, isMasterCompanyCount, isAlliance, isAllianceCount, searchStatus, isCounterCompany, isCounterCompanyCount } = companyState
    let gridRef: any = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const colDef = useMemo(() => {
        let colDef = [];
        if (isMasterCompany) {
            colDef = INITIAL_MASTER_COMP_COL_DEF;
        } else if (isAlliance) {
            colDef = INITIAL_ALLIANCE_COMP_COL_DEF;
        } else {
            colDef = ((operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? INITIAL_COMPANY_COL_DEF?.slice()
                : INITIAL_COMPANY_COL_DEF_AQCT?.slice());
        }

        return commonColumnDef(colDef);
    }, [isAlliance, isMasterCompany, operatingUnit]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        if (companyState.viewState.isAdd) return;

        const selectedRows = e.api.getSelectedRows();
        companyMaintenanceVM.onSelectedCompanys(selectedRows);
    }, [companyMaintenanceVM, companyState.viewState.isAdd])

    const handleGroupButtonClick = useCallback(async (buttonId: string) => {
        setIsLoading(true);
        if (searchStatus && buttonId === searchStatus) {
            await companyMaintenanceVM.onSearch(EMPTY_COMPANY_SEARCH_CRITERIA, false).then((data) => {
                setIsLoading(false);
            }).catch(error => {
                setIsLoading(false);
            }).finally(() => {
                setIsLoading(false);
            })
        } else {
            await companyMaintenanceVM.onGroupButtonClick(buttonId).then((data) => {
                setIsLoading(false);
            }).catch(error => {
                setIsLoading(false);
            }).finally(() => {
                setIsLoading(false);
            })
        }

    }, [companyMaintenanceVM, searchStatus]);

    const isRowSelectable = useCallback((params: any) => {
        if (companyState.viewState.isAdd || companyState.viewState.isEditable) {
            return false;
        } else {
            return true;
        }
    }, [companyState.viewState.isAdd, companyState.viewState.isEditable]);

    const template = useMemo(() => {
        return (
            <>
                <Core.CheckButton
                    checked={isCompany}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isCompanyCount > 999 ? '999+' : isCompanyCount}
                    //disabled={isCompanyCount === 0}
                    size="medium"
                    inputId="isCompany"
                    label={CompanyConstant.COMPANY}
                />
                <Core.CheckButton
                    checked={isCounterCompany}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isCounterCompanyCount > 999 ? '999+' : isCounterCompanyCount}
                    //disabled={isCounterCompanyCount === 0}
                    size="medium"
                    inputId="isCounterCompany"
                    label={CompanyConstant.COUNTER_COMPANY}
                />
                <Core.CheckButton
                    checked={isMasterCompany}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isMasterCompanyCount > 999 ? '999+' : isMasterCompanyCount}
                    //disabled={isMasterCompanyCount === 0}
                    size="medium"
                    inputId="isMasterCompany"
                    label={CompanyConstant.MASTER_COMPANY}
                />
                <Core.CheckButton
                    checked={isAlliance}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleGroupButtonClick(e.target.id) }}
                    showBadge={true}
                    badgeValue={isAllianceCount > 999 ? '999+' : isAllianceCount}
                    //disabled={isAllianceCount === 0}
                    size="medium"
                    inputId="isAlliance"
                    label={CompanyConstant.ALLIANCE_COMPANY}
                />
            </>
        )
    }, [isCompany, isCompanyCount, isAlliance, isAllianceCount, isMasterCompany, isMasterCompanyCount, handleGroupButtonClick, isCounterCompany, isCounterCompanyCount])


    const memoCompanyTable = useMemo(() => {
        return (
            <>
                <NbisTable
                    id='company-maintenance-table'
                    isNewColumnSetting={true}
                    columns={colDef}
                    data={commonTransferRowData(companyState.companys) ?? []}
                    showPaginator={false}
                    editable={false}
                    showHeaderIcons={true}
                    showAddIcon={false}
                    showDeleteButton={false}
                    showReloadIcon={false}
                    isScrollHighlighted={true}
                    selectionMode={false}
                    rowSelection={"multiple"}
                    isRowHighligted={true}
                    handleSelectionChanged={handleSelectionChange}
                    onRowDoubleClick={(e: any) => { companyMaintenanceVM.onDetail(e.data) }}
                    gridHeight="customHeight"
                    customHeight="calc(100vh - 125px)"
                    ref={gridRef}
                    handleRowSelectable={isRowSelectable}
                    headerLabel={
                        <GroupButtons
                            alignment="single-selection"
                            errorMessage=""
                            label=""
                            children={template}
                        />
                    }
                />
            </>
        );
    }, [colDef, companyState.companys, handleSelectionChange, isRowSelectable, template, companyMaintenanceVM])

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            if (companyState.viewState.isAdd) {
                gridRef.current?.gridRef.current.api?.deselectAll();
            }
            gridRef.current.gridRef.current.api.redrawRows();
        }
    }, [companyState.viewState.isAdd, companyState.viewState.isEditable])

    return <><TableWrapper>
        {isLoading && <Loader Indicator="Spinner" size="Medium" />}
        {memoCompanyTable}
    </TableWrapper></>;
}

export default memo(CompanyTablePanel);
